import {MapLayer} from "@esWebAPI/es/map/layer/MapLayer";
import {Map} from "azure-maps-control";
import {ILayerView, LayerInfo, LayerUpdateMetadata} from "@esWebAPI/es/model/Layer";
import {DatasourceManager} from "@esWebAPI/es/map/module/DatasourceManager";
import {IEventChannel} from "@esWebAPI/es/model/Models";
import {PointLayer} from "@esWebAPI/es/map/layer/PointLayer";
import {AzureImageSpriteManager} from "@esWebAPI/es/map/layer/AzureImageSpriteManager";
import {ThematicStylerFactory} from "@esWebAPI/es/map/styler/ThematicStylerFactory";
import { ExpressionFilterSpecification } from "@maplibre/maplibre-gl-style-spec";
import {StopSymbolLayer} from "@esWebAPI/es/map/layer/route/StopSymbolLayer";
import {ScheduleModule, IRoutingModule} from "@esWebAPI/es/map/module/ScheduleModule";
import {PointAndPolygonLayer} from "@esWebAPI/es/map/layer/analysis/PointAndPolygonLayer";
import _ from "lodash";
import {AzureLabelLayer} from "@esWebAPI/es/map/layer/azure/AzureLabelLayer";
import {ColorThematic} from "@esWebAPI/es/model/Thematic";


export class RouteLayer extends MapLayer {

    private readonly schedule: IRoutingModule;
    private readonly analysis: any;

    private _pointLayer: PointLayer|PointAndPolygonLayer|undefined;
    private _stopLayer: StopSymbolLayer|undefined;

    private readonly spriteManager: AzureImageSpriteManager;
    private readonly stylerFactory: ThematicStylerFactory;

    private bindUpdate = _.debounce(this.onModelChange.bind(this), 600,{
        trailing: true
    });


    constructor(map: Map, layerView: ILayerView, layerInfo: LayerInfo,
                datasourceManager: DatasourceManager,
                spriteManager: AzureImageSpriteManager,
                stylerFactory: ThematicStylerFactory,
                schedule: IRoutingModule,
                analysis: any,
                eventChannel: IEventChannel) {
        super(map, layerView, layerInfo, datasourceManager, eventChannel);

        this.schedule = schedule;
        this.analysis = analysis;
        this.spriteManager = spriteManager;
        this.stylerFactory = stylerFactory

        this.createLayers().then(() => this.onReady.resolve());
    }

    private async createLayers() {

        if (this.analysis && ["WithinDistanceAnalysis", "DriveTimeAnalysis"].includes(this.analysis.$type) &&
            this.analysis.generatedAggregateLayerViewId === this.layerView.id) {
            this._pointLayer = new PointAndPolygonLayer(this.map, this.layerView, this.layerInfo,
                this.datasourceManager, this.spriteManager, this.stylerFactory, this.eventChannel);
        } else {
            this._pointLayer = new PointLayer(this.map, this.layerView, this.layerInfo,
                this.datasourceManager, this.spriteManager, this.stylerFactory, this.eventChannel);
        }

        this._stopLayer = new StopSymbolLayer(this.map, this.layerView, this.layerInfo,
            this.datasourceManager, this.spriteManager, this.stylerFactory, this.schedule, this.eventChannel);

        await this._pointLayer.onReady;
        await this._stopLayer.onReady;

        if (this.schedule instanceof ScheduleModule) {
            this.schedule.getEventChannel()
                .addListener("onAddService", this.bindUpdate, this)
                .addListener("onRemoveService", this.bindUpdate, this)
                .addListener("onEditService", this.bindUpdate, this)
                .addListener("onClearSchedule", this.bindUpdate, this);

            es.api.getEventChannel().addListener("onUpdateScheduleShifts", this.bindUpdate, this);
        } else {
            this.schedule.getEventChannel().addListener("onModelChange", this.bindUpdate);
        }
    }

    private onModelChange() {
        if (this._pointLayer){
            // We pass isCriteriaChanged: true to force re-recreation of the Datasource
            if (this._pointLayer instanceof PointAndPolygonLayer) {
                this._pointLayer.pointLayer?.update({isCriteriaChanged: true});
            } else {
                this._pointLayer.update({isCriteriaChanged: true});
            }
        }

        if (this._stopLayer) {
            this._stopLayer.update({});
        }
    }

    public destroy(): void {
        super.destroy();
        this._pointLayer?.destroy();
        this._stopLayer?.destroy();

        if (this.schedule instanceof ScheduleModule) {
            this.schedule.getEventChannel()
                .removeListener("onAddService", this.bindUpdate, this)
                .removeListener("onRemoveService", this.bindUpdate, this)
                .removeListener("onEditService", this.bindUpdate, this)
                .removeListener("onClearSchedule", this.bindUpdate, this);

            es.api.getEventChannel().removeListener("onUpdateScheduleShifts", this.bindUpdate, this);
        } else {
            this.schedule.getEventChannel()
                .removeListener("onModelChange", this.bindUpdate, this);
        }
        this.bindUpdate.cancel();
    }

    protected getColorThematicFilter(): ExpressionFilterSpecification | undefined {
        return undefined;
    }

    protected getSymbolThematicFilter(): ExpressionFilterSpecification | undefined {
        return undefined;
    }

    protected hideFeature(): void {}

    protected showFeatures(): void {}

    public update(_layerUpdateMetadata: LayerUpdateMetadata): Promise<void> {
        return Promise.resolve(undefined);
    }

    public async showPointsScheduledForDayOnly(start: number, end: number) {
        this._pointLayer?.hide();
        await this._stopLayer?.showOnlyForDay(start, end);
    }

    public showAllStops() {
        this._stopLayer?.showAllStops();
    }

    public showUnscheduledPoints() {
        this._pointLayer?.show();
    }


    public get pointLayer(): PointLayer | PointAndPolygonLayer | undefined {
        return this._pointLayer;
    }

    public get stopLayer(): StopSymbolLayer | undefined {
        return this._stopLayer;
    }

    public getLayerIds(): string[] {
        const layerIds = [];
        if (this._pointLayer) {
            layerIds.push(...this._pointLayer.getLayerIds());
        }
        if (this._stopLayer) {
            layerIds.push(...this._stopLayer.getLayerIds());
        }
        return layerIds;
    }

    public getActiveAzureLayerId(): string | undefined {
        // todo: not sure which. do we need to handle both? test
        return this._stopLayer?.getActiveAzureLayerId() || this._pointLayer?.getActiveAzureLayerId();
    }

    public async getClusterLeavesFeats(clusterId: number): Promise<any[]> {
        return ((this._pointLayer) as PointLayer).getClusterLeavesFeats(clusterId); // todo: handle PointAndPolygonLayer
    }

    public async getClusterLeavesFeatIds(clusterId: number): Promise<number[]> {
        return ((this._pointLayer) as PointLayer).getClusterLeavesFeatIds(clusterId); // todo: handle PointAndPolygonLayer
    }

    public get labelLayer(): AzureLabelLayer {
        return this._pointLayer?.labelLayer;
    }

    public get colorThematic(): ColorThematic | undefined {
        return this._pointLayer?._colorThematic;
    }

    public hide() {
        this._stopLayer?.hide();
        this._pointLayer?.hide();
    }

    public show() {
        this._stopLayer?.show();
        this._pointLayer?.show();
    }
}
